import { Link } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { formatDistanceToNowLang, formatNumber } from "lopermut"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  OthersDynamicSegmentName,
  PredefinedSegmentGroups,
  SegmentGroupResponseDataSchema,
} from "schema"
import z from "zod"
import { RfmMiniGrid } from "./RfmMiniGrid"
import { SegmentGroupSimpleChart } from "./SegmentGroupSimpleChart"

const MAX_CHART_SLICES = 5

type Props = {
  segmentGroup: z.infer<typeof SegmentGroupResponseDataSchema>
}

export function SegmentGroupCard({ segmentGroup }: Props) {
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  const { t } = useTranslation()

  const filteredSegments = useMemo(() => {
    return segmentGroup.segments.filter((segment) => segment.name !== OthersDynamicSegmentName)
  }, [segmentGroup.segments])

  const chartData = useMemo(() => {
    return filteredSegments
      .map((segment) => ({
        name: getSegmentTranslation(segment.name, segmentGroup),
        value: segment.count,
      }))
      .slice(0, MAX_CHART_SLICES)
  }, [filteredSegments, getSegmentTranslation, segmentGroup])

  const moreSegmentsCount = useMemo(() => {
    return filteredSegments.length - chartData.length
  }, [filteredSegments.length, chartData.length])

  const customersInSegmentCount = useMemo(() => {
    return segmentGroup.segments.reduce((acc, segment) => acc + segment.count, 0)
  }, [segmentGroup.segments])

  const shouldShowRfmGrid =
    segmentGroup.isPredefined &&
    segmentGroup.name === PredefinedSegmentGroups.rfm &&
    customersInSegmentCount > 0

  return (
    <Link
      to="/customers/segments/$id"
      params={{ id: segmentGroup.id }}
      className="group rounded-lg flex-1 h-full"
    >
      <Card
        key={segmentGroup.id}
        className="w-full h-full p-6 flex flex-col gap-4 cursor-pointer group-hover:bg-muted group-active:bg-muted/70 transition-colors"
      >
        <div className="flex flex-row items-start justify-between">
          <div className="flex flex-col gap-0">
            <h2 className="text-lg font-medium">{getSegmentGroupTranslation(segmentGroup)}</h2>
            <span className="text-base text-muted-foreground">
              {t("segments.list.count", { count: segmentGroup.segments.length })}
            </span>
            <span className="text-base text-muted-foreground">
              {t("segments.list.customersCount", {
                formattedCount: formatNumber({ amount: customersInSegmentCount }),
                formattedTotalCount: formatNumber({ amount: segmentGroup.customersCount }),
              })}
            </span>
          </div>
          {segmentGroup.isPredefined && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="secondary" className="w-fit">
                  {t("segments.predefined.label")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("segments.predefined.tooltip")}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {shouldShowRfmGrid ? (
          <RfmMiniGrid segmentGroup={segmentGroup} />
        ) : (
          <SegmentGroupSimpleChart chartData={chartData} />
        )}
        <div className="flex gap-1 flex-col-reverse sm:flex-row-reverse sm:items-center items-start sm:justify-between">
          <span className="text-muted-foreground text-sm">
            {t("segments.list.updatedAt", {
              updatedAt: formatDistanceToNowLang(segmentGroup.updatedAt, { addSuffix: true }),
            })}
          </span>
          {!shouldShowRfmGrid && moreSegmentsCount > 0 && (
            <span className="text-muted-foreground text-sm">
              {t("segments.list.moreSegments", { count: moreSegmentsCount })}
            </span>
          )}
        </div>
      </Card>
    </Link>
  )
}
